import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHandleSignInCallback, useLogto } from '@logto/react';
import { makeClient, type ApiClient } from './api';
import { config } from './config';
import { UsersView } from './components/UsersView';
import { UserDetail } from './components/UserDetail';
import { AccountView } from './components/AccountView';
import { PaymentsView } from './components/PaymentsView';
import { PricesView } from './components/PricesView';
import { BillingEventsView } from './components/BillingEventsView';
import { LicensesView } from './components/LicensesView';

/** Hash-based routing keeps the SPA a pure static bundle: nginx serves index.html
 *  for everything and the browser handles route changes. Two routes today:
 *    #/                 users list
 *    #/users/<uuid>     detail for one user
 */
type Section = 'payments' | 'prices' | 'billing-events' | 'licenses' | 'account';
type Route = { kind: 'users' } | { kind: 'user'; id: string } | { kind: Section };

const SECTIONS: Section[] = ['payments', 'prices', 'billing-events', 'licenses', 'account'];

function parseHash(): Route {
  const raw = window.location.hash.slice(1);
  const match = raw.match(/^\/users\/([0-9a-f-]{36})/i);
  if (match) return { kind: 'user', id: match[1]! };
  const section = SECTIONS.find((name) => raw === `/${name}`);
  if (section) return { kind: section };
  return { kind: 'users' };
}

function hashFor(route: Route): string {
  if (route.kind === 'users') return '/';
  if (route.kind === 'user') return `/users/${route.id}`;
  return `/${route.kind}`;
}

function useRoute(): [Route, (route: Route) => void] {
  const [route, setRoute] = useState<Route>(parseHash());
  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const navigate = useCallback((next: Route) => {
    const hash = hashFor(next);
    if (window.location.hash !== `#${hash}`) window.location.hash = hash;
  }, []);
  return [route, navigate];
}

export function App() {
  // The Logto SPA sends the user to /callback with the code + state. The SDK's
  // hook handles it in place; after it resolves we bounce back to the root so
  // the URL bar isn't left holding the code.
  if (window.location.pathname === '/callback') return <SignInCallback />;
  return <Shell />;
}

function SignInCallback() {
  const { isLoading } = useHandleSignInCallback(() => {
    // History replacement rather than a navigation so the browser back button
    // doesn't return the user to a code-carrying URL that no longer works.
    window.history.replaceState(null, '', '/');
    window.location.reload();
  });
  return <div className="centered">{isLoading ? 'Signing in…' : 'Redirecting…'}</div>;
}

function Shell() {
  const { isAuthenticated, isLoading, signIn, signOut, getIdTokenClaims, getAccessToken } = useLogto();
  const [route, navigate] = useRoute();
  const [displayName, setDisplayName] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    void getIdTokenClaims().then((claims) => {
      setDisplayName(claims?.name ?? claims?.username ?? claims?.sub ?? null);
    });
  }, [isAuthenticated, getIdTokenClaims]);

  // Pin the API client. `useLogto()` returns a fresh `getAccessToken`
  // reference on every render, so a naive `useMemo(..., [getAccessToken])`
  // built a new client every render, which broke every downstream `useEffect`
  // that depended on `client` — kicking off an infinite fetch loop against
  // /api/v1/admin/users that filled the API's rate-limit bucket in seconds.
  // The ref keeps the client stable while its token getter always sees the
  // latest function.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const client = useMemo<ApiClient>(
    () => makeClient(() => getAccessTokenRef.current(config.apiResource)),
    [],
  );

  // `@logto/react` sets its shared `isLoading` flag around *every* proxied
  // SDK call, including `getAccessToken()`. Hiding the authenticated shell
  // here therefore unmounted UsersView while its request was obtaining a
  // token; once the token arrived the view mounted again and immediately
  // started the same request, producing an infinite fetch/remount loop.
  // Only use the full-page loading state while Logto is establishing the
  // initial (unauthenticated) session. Keep authenticated views mounted while
  // individual SDK operations are in flight.
  if (isLoading && !isAuthenticated) return <div className="centered">Loading…</div>;

  if (!isAuthenticated) {
    if (!config.logtoAppId) {
      return (
        <div className="centered">
          <h2>Not configured</h2>
          <p className="muted">
            The container is missing <kbd>LOGTO_APP_ID</kbd>. Update the compose file and redeploy.
          </p>
        </div>
      );
    }
    return (
      <div className="centered">
        <h2>DeskTouch Ops</h2>
        <p className="muted">Sign in with your DeskTouch admin account to continue.</p>
        <button className="primary" onClick={() => signIn(`${window.location.origin}/callback`)}>
          Sign in with Logto
        </button>
      </div>
    );
  }

  return (
    <>
      <header className="app-header">
        <h1>DeskTouch&nbsp;Ops</h1>
        <nav>
          {(
            [
              ['users', 'Users'],
              ['payments', 'Payments'],
              ['prices', 'Prices'],
              ['billing-events', 'PayU events'],
              ['licenses', 'Licences'],
              ['account', 'My account'],
            ] as Array<[Route['kind'], string]>
          ).map(([kind, label]) => (
            <a
              key={kind}
              className={route.kind === kind || (kind === 'users' && route.kind === 'user') ? 'active' : ''}
              href={`#${hashFor({ kind } as Route)}`}
              onClick={(event) => {
                event.preventDefault();
                navigate({ kind } as Route);
              }}
            >
              {label}
            </a>
          ))}
        </nav>
        <span className="who">{displayName}</span>
        <button
          onClick={() => signOut(window.location.origin)}
          title="Sign out and clear the local session"
        >
          Sign out
        </button>
      </header>
      <main>
        {route.kind === 'users' && (
          <UsersView client={client} onOpen={(id) => navigate({ kind: 'user', id })} />
        )}
        {route.kind === 'user' && (
          <UserDetail
            client={client}
            userId={route.id}
            onBack={() => navigate({ kind: 'users' })}
          />
        )}
        {route.kind === 'payments' && <PaymentsView client={client} onOpenUser={(id) => navigate({ kind: 'user', id })} />}
        {route.kind === 'prices' && <PricesView client={client} />}
        {route.kind === 'billing-events' && <BillingEventsView client={client} />}
        {route.kind === 'licenses' && <LicensesView client={client} />}
        {route.kind === 'account' && <AccountView client={client} />}
      </main>
    </>
  );
}
